import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import { Route, RouteConfigLoadStart, Router, Routes } from '@angular/router';
import { filter } from 'rxjs';

import { Mut } from '@efficiently/shared-util-types';

import { recoverOnImportFailure } from './recover-on-import-failure';

type LoadChildrenCallback = NonNullable<Route['loadChildren']>;
type LoadComponentCallback = NonNullable<Route['loadComponent']>;
type ImportFn = () => Promise<unknown>;

// Mark wrapped functions to avoid double-wrapping
const wrappedFunctions = new WeakSet<ImportFn>();

function wrapWithRecovery(fn: ImportFn): ImportFn {
	if (wrappedFunctions.has(fn)) {
		return fn;
	}

	try {
		const wrapped = recoverOnImportFailure(fn);
		wrappedFunctions.add(wrapped);
		return wrapped;
	} catch {
		return fn;
	}
}

function wrapRouteImports(route: Mut<Route>): void {
	if (route.loadChildren) {
		route.loadChildren = wrapWithRecovery(route.loadChildren as ImportFn) as LoadChildrenCallback;
	}

	if (route.loadComponent) {
		route.loadComponent = wrapWithRecovery(route.loadComponent as ImportFn) as LoadComponentCallback;
	}
}

function normalizeRoutesInPlace(routes: Mut<Routes>): void {
	for (const route of routes) {
		wrapRouteImports(route);

		if (route.children) {
			normalizeRoutesInPlace(route.children);
		}
	}
}

function wrapRouterConfig(): void {
	const router = inject(Router);
	normalizeRoutesInPlace(router.config as Mut<Routes>);

	router.events.pipe(filter((e): e is RouteConfigLoadStart => e instanceof RouteConfigLoadStart)).subscribe(event => {
		wrapRouteImports(event.route as Mut<Route>);
	});
}

export const ROUTER_CONFIG_WRAPPER: EnvironmentProviders = provideAppInitializer(wrapRouterConfig);
